import React, { createContext, useState, useEffect, useContext, ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  onAuthStateChanged,
  signOut,
  User as FirebaseUser,
  getIdToken,
  getIdTokenResult
} from 'firebase/auth';
import { auth, db } from '../config/firebase';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import axios from 'axios';

interface User {
  id: string;
  email: string;
  name: string;
  role: 'franchisee' | 'admin' | 'manager';
  shops?: string[];
  permissions: string[];
}

interface AuthContextType {
  user: User | null;
  firebaseUser: FirebaseUser | null;
  token: string | null;
  login: (email: string, password: string) => Promise<void>;
  logout: () => void;
  refreshToken: () => Promise<void>;
  isLoading: boolean;
  error: string | null;
  hasPermission: (permission: string) => boolean;
  switchShop: (shopId: string) => void;
}

const AuthContext = createContext<AuthContextType>({
  user: null,
  firebaseUser: null,
  token: null,
  login: async () => {},
  logout: () => {},
  refreshToken: async () => {},
  isLoading: false,
  error: null,
  hasPermission: () => false,
  switchShop: () => {},
});

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [firebaseUser, setFirebaseUser] = useState<FirebaseUser | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();

  // Determine user role based on email domain
  const determineRoleFromEmail = (email: string): 'admin' | 'manager' | 'franchisee' => {
    if (email.includes('@wayneia.com') || email === 'bbob@wayneia.com') {
      return 'admin';
    } else if (email.includes('@temple.com') || email.includes('@woodway.com')) {
      return 'manager';
    } else {
      return 'franchisee';
    }
  };

  // Get permissions based on role
  const getPermissionsForRole = (role: string): string[] => {
    switch (role) {
      case 'admin':
        return ['view_all', 'edit_all', 'manage_users', 'view_analytics', 'manage_system'];
      case 'manager':
        return ['view_shop', 'edit_shop', 'manage_staff', 'view_analytics'];
      case 'franchisee':
        return ['view_all_shops', 'view_analytics', 'view_reports'];
      default:
        return [];
    }
  };

  // Fetch or create user profile in Firestore
  const fetchOrCreateUserProfile = async (fbUser: FirebaseUser): Promise<User> => {
    const userRef = doc(db, 'users', fbUser.uid);
    const userDoc = await getDoc(userRef);

    if (userDoc.exists()) {
      // User exists, return their data
      return userDoc.data() as User;
    } else {
      // New user, create profile
      const role = determineRoleFromEmail(fbUser.email || '');
      const permissions = getPermissionsForRole(role);

      const newUser: User = {
        id: fbUser.uid,
        email: fbUser.email || '',
        name: fbUser.displayName || fbUser.email?.split('@')[0] || 'User',
        role,
        permissions,
        shops: role === 'admin' ? ['temple', 'woodway'] :
               role === 'manager' && fbUser.email?.includes('temple') ? ['temple'] :
               role === 'manager' && fbUser.email?.includes('woodway') ? ['woodway'] :
               ['temple', 'woodway']
      };

      // Save to Firestore
      await setDoc(userRef, newUser);

      return newUser;
    }
  };

  // Firebase auth state observer
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (fbUser) => {
      if (fbUser) {
        try {
          // Get ID token
          const idToken = await getIdToken(fbUser);
          setToken(idToken);
          setFirebaseUser(fbUser);

          // Set axios default header
          axios.defaults.headers.common['Authorization'] = `Bearer ${idToken}`;

          // Fetch or create user profile
          const userProfile = await fetchOrCreateUserProfile(fbUser);
          setUser(userProfile);

          // Navigate based on role
          if (userProfile.role === 'admin') {
            navigate('/admin/dashboard');
          } else if (userProfile.role === 'manager') {
            navigate('/manager/dashboard');
          } else {
            navigate('/franchisee/dashboard');
          }
        } catch (err) {
          console.error('Error setting up user:', err);
          setError('Failed to load user profile');
        }
      } else {
        // User is signed out
        setUser(null);
        setFirebaseUser(null);
        setToken(null);
        delete axios.defaults.headers.common['Authorization'];
        navigate('/login');
      }
      setIsLoading(false);
    });

    return () => unsubscribe();
  }, [navigate]);

  const login = async (email: string, password: string) => {
    // Login is handled in Login component directly with Firebase
    // This method is kept for backwards compatibility
    throw new Error('Use Firebase signInWithEmailAndPassword directly');
  };

  const logout = async () => {
    try {
      await signOut(auth);
      // Firebase auth state observer will handle the rest
    } catch (err) {
      console.error('Logout error:', err);
      setError('Failed to logout');
    }
  };

  const refreshToken = async () => {
    if (firebaseUser) {
      try {
        const newToken = await getIdToken(firebaseUser, true);
        setToken(newToken);
        axios.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
      } catch (err) {
        console.error('Token refresh error:', err);
        throw err;
      }
    }
  };

  const hasPermission = (permission: string) => {
    if (!user) return false;
    return user.permissions?.includes(permission) || false;
  };

  const switchShop = (shopId: string) => {
    // Implement shop switching logic
    console.log('Switching to shop:', shopId);
    // You could update a current shop in context or local storage
  };

  const value: AuthContextType = {
    user,
    firebaseUser,
    token,
    login,
    logout,
    refreshToken,
    isLoading,
    error,
    hasPermission,
    switchShop,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export default AuthContext;